import type { ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, History } from 'lucide-react';
import { EmptyState } from '@/components/EmptyState';
import { SkeletonRegion, SkeletonText } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import { formatarDataHora, formatarDistancia } from '@/lib/format';
import { NomeDePessoa, useDicionarioDePessoas } from '@/features/tickets/pessoas';
import { chaves } from '@/features/tickets/tickets.api';
import type { Chamado } from '@/features/tickets/types';
import { AvisoDeFalha, Chave } from '@/features/workflow/componentes';
import { classificarFalha } from '@/features/workflow/recusa';
import {
  ROTULO_DE_ENTREGA,
  ROTULO_DE_RELOGIO,
  type EfeitoDeTransicao,
  type RegistroDeTransicao,
} from '@/features/workflow/tipos';
import { chavesDeFluxo, obterHistorico } from '@/features/workflow/workflow.api';

/**
 * A trilha do fluxo — quem, quando, de onde para onde, e por quê.
 *
 * O `TransitionLog` é **append-only**: não tem `version` nem `deletedAt`
 * porque apagar a linha seria apagar a prova de que a transição aconteceu, e é
 * dela que pende a idempotência dos efeitos. A tela trata a lista como isso
 * mesmo — leitura, sem nenhuma ação de edição.
 *
 * A ordem é a de `seq`, que é a do servidor: várias transições da mesma
 * transação compartilham o instante do banco, e ordenar por `at` na tela
 * embaralharia justamente os saltos automáticos, que são os que mais precisam
 * ser lidos em ordem.
 */
export function HistoricoDeFluxo({ chamado }: { chamado: Chamado }) {
  const cliente = useQueryClient();

  const consulta = useQuery({
    queryKey: chavesDeFluxo.historico(chamado.id),
    queryFn: () => obterHistorico(chamado.id),
    enabled: chamado.workflowVersionId !== null,
    /**
     * Sem retentativa automática.
     *
     * A trilha é leitura de um registro append-only: repeti-la não muda a
     * resposta, e a retentativa invisível só adia a mensagem. Pior: quando o
     * navegador se julga offline, o TanStack **pausa** a retentativa em vez de
     * falhar, e o painel fica num esqueleto que nunca termina. Falhar rápido e
     * oferecer o botão "Recarregar" põe a retentativa na mão de quem lê.
     */
    retry: false,
  });

  const pessoas = useDicionarioDePessoas((consulta.data ?? []).map((linha) => linha.actorId));

  if (chamado.workflowVersionId === null) {
    return (
      <EmptyState
        icon={<History className="h-7 w-7" aria-hidden />}
        title="Este chamado não segue nenhum fluxo"
        description="A trilha de transições só existe para chamados vinculados a um workflow. A situação deste aqui muda pelo seletor, e o que ficou registrado está na auditoria."
      />
    );
  }

  if (consulta.isPending) {
    return (
      <SkeletonRegion label="Carregando a trilha do fluxo" className="space-y-3">
        <SkeletonText lines={4} />
      </SkeletonRegion>
    );
  }

  if (consulta.isError) {
    return (
      <AvisoDeFalha
        falha={classificarFalha(consulta.error)}
        aoRecarregar={() => void cliente.invalidateQueries({ queryKey: chaves.raiz })}
      />
    );
  }

  if (consulta.data.length === 0) {
    return (
      <EmptyState
        icon={<History className="h-7 w-7" aria-hidden />}
        title="Nenhuma transição ainda"
        description="O chamado está no estado inicial do fluxo. A primeira ação que alguém tomar aparece aqui."
      />
    );
  }

  return (
    <ol className="space-y-0">
      {consulta.data.map((registro, indice) => (
        <Passagem
          key={registro.id}
          registro={registro}
          ultima={indice === consulta.data.length - 1}
          nome={<NomeDePessoa id={registro.actorId} pessoas={pessoas} semId="o próprio sistema" />}
        />
      ))}
    </ol>
  );
}

/**
 * Uma passagem.
 *
 * O ator vem do token de quem executou; `null` significa transição sem gente
 * por trás — automática ou disparada por evento —, e "o próprio sistema" é
 * mais honesto do que um traço.
 */
function Passagem({
  registro,
  ultima,
  nome,
}: {
  registro: RegistroDeTransicao;
  ultima: boolean;
  nome: ReactNode;
}) {
  const campos = Object.entries(registro.fields ?? {});

  return (
    <li className="relative flex gap-3 pb-5 last:pb-0">
      {/* O fio que liga as passagens. Não é `border-left` do `li` porque
          precisa parar no último item, e não atravessá-lo. */}
      {!ultima ? (
        <span aria-hidden className="absolute left-[7px] top-4 h-full w-px bg-border" />
      ) : null}
      <span
        aria-hidden
        className={cn(
          'mt-1 h-3.5 w-3.5 flex-none rounded-full ring-4 ring-background',
          registro.terminal ? 'bg-success' : 'bg-primary',
        )}
      />

      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-sm font-medium">{registro.transitionKey}</span>
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Chave valor={registro.fromState} />
            <ArrowRight className="h-3 w-3" aria-hidden />
            <Chave valor={registro.toState} />
          </span>
        </div>

        <p className="text-xs text-muted-foreground">
          {nome} · <time title={formatarDataHora(registro.at)}>{formatarDistancia(registro.at)}</time>
          {' · '}
          {ROTULO_DE_RELOGIO[registro.slaClock]}
          {registro.terminal ? ' · encerrou o fluxo' : ''}
        </p>

        {registro.reason ? (
          <p className="rounded-md border-l-2 border-border bg-muted/40 px-3 py-2 text-sm">
            {registro.reason}
          </p>
        ) : null}

        {campos.length > 0 ? (
          <dl className="grid gap-x-4 gap-y-1 text-xs sm:grid-cols-[auto_1fr]">
            {campos.map(([campo, valor]) => (
              <div key={campo} className="contents">
                <dt className="font-mono text-muted-foreground">{campo}</dt>
                <dd className="truncate" title={String(valor)}>
                  {String(valor)}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}

        {registro.effects.length > 0 ? <Efeitos itens={registro.effects} /> : null}
      </div>
    </li>
  );
}

/**
 * O que a transição causou.
 *
 * `DEAD` não é falha de execução: é efeito do catálogo que ainda não tem
 * executor nesta fase. A linha fica visível de propósito — o servidor a grava
 * como pendência consultável em vez de deixar o efeito sumir em silêncio —, e
 * escondê-la aqui desfaria isso.
 */
function Efeitos({ itens }: { itens: readonly EfeitoDeTransicao[] }) {
  return (
    <ul className="flex flex-wrap gap-1.5">
      {itens.map((efeito) => (
        <li key={`${efeito.transitionLogId}-${efeito.effectIndex}`}>
          <span
            title={efeito.lastError ?? undefined}
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ring-1 ring-inset',
              efeito.status === 'DELIVERED' && 'bg-success/10 text-success ring-success/25',
              efeito.status === 'PENDING' && 'bg-muted text-muted-foreground ring-border',
              efeito.status === 'FAILED' && 'bg-destructive/10 text-destructive ring-destructive/25',
              efeito.status === 'DEAD' && 'bg-warning/10 text-warning ring-warning/25',
            )}
          >
            <span className="font-mono">{efeito.type}</span>
            <span className="opacity-80">{ROTULO_DE_ENTREGA[efeito.status]}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}
